import { AfterViewInit, Component, ElementRef, HostBinding, HostListener, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { AgEditorComponent } from 'ag-grid-angular';
import { ICellEditorParams } from 'ag-grid-community';
import { isNil, isNull } from 'lodash-es';
import { ME_Fit } from 'me-fit-typings';
import { WorkoutStepData } from 'src/app/state/workout.state.service';

export type InputTypeCellFocusOutDirection = 'previous' | 'next';

export interface AgGridValueEditorParams {
  defaultValue?: number;
}

/**
 * Special component for editing 'value' on WorkoutStep level. Will take "repititions" or "interval" as input
 * and render a proper input field for that.
 */
@Component({
  selector: 'me-ag-grid-value-editor',
  templateUrl: './ag-grid-value-editor.component.html',
  styleUrls: ['./ag-grid-value-editor.component.css']
})
export class AgGridValueEditorComponent implements AgEditorComponent, AfterViewInit {

  private params: ICellEditorParams<WorkoutStepData, number>;

  valueType: ME_Fit.WorkoutStepType;

  formCtrl = new FormControl<string>('0');

  private apiInstance: ICellEditorParams<WorkoutStepData, number, AgGridValueEditorParams>['api'];

  @HostBinding('class.large-height')
  get isForIntervalValue() {
    return this.valueType === 'interval';
  }

  @ViewChild('input')
  public nativeInput: ElementRef<HTMLInputElement>;

  lastFocusOutDirection: InputTypeCellFocusOutDirection | undefined;

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent) {
    console.log('[AgGridValueEditorComponent] key down', event);
    if (event?.key == 'Tab') {
      this.lastFocusOutDirection = event?.shiftKey ? 'previous' : 'next';
    }
  }

  @HostListener('window:keyup', ['$event'])
  onKeyup(event: KeyboardEvent) {
    if (event.key !== 'Tab') {
      return;
    }

    console.log('[AgGridValueEditorComponent] key up', event,);

    this.handleTabNavigation();
  }


  constructor(private el: ElementRef<HTMLElement>) { }

  ngAfterViewInit(): void {
    // focus on the input if its present
    if (this.nativeInput) {
      setTimeout(() => {
        this.nativeInput.nativeElement.focus();

        this.nativeInput.nativeElement.setSelectionRange(0, this.nativeInput.nativeElement.value.length);
      });
    }
  }


  ngOnInit() {
  }

  agInit(params: ICellEditorParams<WorkoutStepData, number, AgGridValueEditorParams>): void {
    this.apiInstance = params.api;
    console.log('[AgGridValueEditorComponent:agInit]', params);
    this.params = params;

    this.valueType = params.data?.type ?? 'repetitions';

    const numericValue = params.value ?? (this.params['defaultValue'] as AgGridValueEditorParams['defaultValue']);

    this.formCtrl.setValue(`${numericValue}`);
  }

  getValue() {
    const value = this.formCtrl.value;

    // If we have a default avlue, and this is nil, we will use the default value
    if (isNil(value) && !isNil(this.params['defaultValue'])) {
      return this.params['defaultValue'];
    }

    // We dont want any fields to ever be null as it doesnt work nicely with our database (prisma)
    //
    if (isNull(value)) {
      return undefined;
    }

    return parseInt(value);
  }

  handleTabNavigation() {
    const focusedElement = document.activeElement;
    if (!this.el.nativeElement.contains(focusedElement)) {
      console.log('[AgGridValueEditorComponent] focused element is outside this component');
      if (this.lastFocusOutDirection === 'next') {
        this.apiInstance.tabToNextCell();
      } else if (this.lastFocusOutDirection === 'previous') {
        // TODO fix this as backwards navigation is not working
        this.apiInstance.tabToPreviousCell();
      }
    }
  }
}
